import { Children, FC, ReactNode } from "react";

import { Grid, Stack } from "@mui/material";

import { ThemedFundraiserSkeleton } from "./ThemedFundraiserSkeleton";

import { FormBlock, FormBlockProps } from "@/forms-design-system/FormBlock";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormSearchField } from "@/forms-design-system/FormSearchField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Trans, useTranslate } from "@/hooks/useTranslate";

export type ThemedFundraisersListBlockProps = Omit<FormBlockProps, "ref"> & {
  isLoading?: boolean;
  canSearch?: boolean;
  children?: ReactNode;
  onViewAllClick: () => void;
  searchText: string;
  onSearchTextChange: (searchText: string) => void;
  showViewAllButton?: boolean;
  mode?: "show-team-and-individual-campaigns" | "show-only-individual-campaigns";
};

export const ThemedFundraisersListBlock: FC<ThemedFundraisersListBlockProps> = ({
  isLoading = false,
  searchText,
  onSearchTextChange,
  children,
  onViewAllClick,
  showViewAllButton = false,

  mode = "show-team-and-individual-campaigns",
  ...rest
}) => {
  const { t } = useTranslate();

  const renderFundraisers = () => {
    if (isLoading) {
      return (
        <Grid container spacing={1.5}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <ThemedFundraiserSkeleton />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!!searchText && Children.count(children) === 0) {
      return (
        <FormTypography variant="body2" vibe="supershy">
          <Trans
            space="donationForm"
            i18nKey="peerToPeer.fundraisers.searchNoResults"
            components={{ sb: <FormTypography variant="subtitle2" vibe="supershy" component="span" /> }}
          />
        </FormTypography>
      );
    }

    return (
      <Grid container spacing={1.5}>
        {Children.map(children, (child, index) => (
          <Grid item xs={12} sm={6} key={index}>
            {child}
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <FormBlock variant="card" fullWidth {...rest}>
      <Stack alignItems="flex-start" gap={2}>
        <FormTypography variant="subtitle1" vibe="supershy">
          {mode === "show-team-and-individual-campaigns"
            ? t("donationForm", "peerToPeer.fundraisersCard.title")
            : t("donationForm", "peerToPeer.teamMemberCampaigns.title")}
        </FormTypography>
        {showViewAllButton && (
          <FormSearchField
            value={searchText}
            onChange={onSearchTextChange}
            placeholder={
              mode === "show-team-and-individual-campaigns"
                ? t("donationForm", "peerToPeer.fundraisersCard.searchPlaceholder")
                : t("donationForm", "peerToPeer.teamMemberCampaignsCard.searchPlaceholder")
            }
          />
        )}
        {renderFundraisers()}
        {showViewAllButton && (
          <FormButton variant="outline" size="small" onClick={onViewAllClick}>
            {mode === "show-team-and-individual-campaigns"
              ? t("donationForm", "peerToPeer.fundraisersCard.viewAll")
              : t("donationForm", "peerToPeer.teamMemberCampaignsCard.viewAll")}
          </FormButton>
        )}
      </Stack>
    </FormBlock>
  );
};
